import React, { useState } from 'react';

/**
 * Sortable column headers for the requisition tables.
 *
 * One implementation for every list on the Enterprise Requisition tab, because
 * four tables each sorting "their own way" is how one of them ends up ordering
 * ₦100,000 before ₦9,000 while the others do not.
 *
 * The comparison is chosen from the VALUES, not declared per column: the same
 * header ("Est. Amount") holds a number here and a formatted string there, and
 * a column list that has to remember which is which will eventually be wrong.
 */

/** Ascending compare that copes with the three kinds of cell these tables hold. */
const compare = (a, b) => {
  // Blanks always sink, in both directions. A column half-full of em-dashes
  // sorted "ascending" should not open with a screen of nothing.
  const aEmpty = a === null || a === undefined || a === '' || a === '—';
  const bEmpty = b === null || b === undefined || b === '' || b === '—';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;

  // Dates before text: "2026-09-10" and "09-09-2026" both compare wrongly as
  // strings, and every one of these tables has a date column.
  const da = Date.parse(a);
  const db = Date.parse(b);
  if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;

  // Numbers that arrived as strings — "₦53,750.00", "1", "2.50".
  const na = Number(String(a).replace(/[^0-9.-]/g, ''));
  const nb = Number(String(b).replace(/[^0-9.-]/g, ''));
  if (String(a).trim() !== '' && String(b).trim() !== ''
      && Number.isFinite(na) && Number.isFinite(nb)
      && /\d/.test(String(a)) && /\d/.test(String(b))
      && !/[a-z]{2}/i.test(String(a)) && !/[a-z]{2}/i.test(String(b))) {
    return na - nb;
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Sort state only — no rows, no memo, nothing that depends on them.
 *
 * Deliberately split from the sorting itself. These tables build their row
 * projection well below an early `return <Loader/>`, so a hook that needed the
 * rows could not be called without sitting after that return — which React
 * forbids, and which a lint rule catches only if you happen to run it. A hook
 * that takes no rows can live at the top of the component where it belongs.
 */
export function useSortState(initialKey = null, initialDir = 'asc') {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);

  const onSort = (key) => {
    if (key === sortKey) {
      // Third click clears it, so the reader can always get back to the
      // server's own order — newest-first on every one of these lists.
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortKey(null); setSortDir('asc'); }
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return { sortKey, sortDir, onSort };
}

/**
 * Apply that state to rows. A plain function, callable anywhere.
 *
 * `valueOf(row, key)` says what a column holds. Callers sort their rows BEFORE
 * interleaving any expanded-detail rows, so a re-sort cannot separate a row
 * from the panel hanging beneath it.
 */
export function sortRows(rows, state, valueOf) {
  if (!state?.sortKey) return rows;
  // A copy: the caller's array is state elsewhere, and Array.sort mutates.
  const out = [...rows];
  out.sort((x, y) => {
    const r = compare(valueOf(x, state.sortKey), valueOf(y, state.sortKey));
    return state.sortDir === 'asc' ? r : -r;
  });
  return out;
}

/** The two chevrons: dim when the column is idle, one lit when it is sorted. */
const Chevrons = ({ dir }) => (
  <span style={{
    display: 'inline-flex', flexDirection: 'column', lineHeight: 0,
    marginLeft: 6, flexShrink: 0, verticalAlign: 'middle',
  }}>
    <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: 'block' }}>
      <path d="M4 0 L8 5 L0 5 Z" fill="currentColor" opacity={dir === 'asc' ? 1 : 0.35} />
    </svg>
    <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: 'block', marginTop: 2 }}>
      <path d="M4 5 L0 0 L8 0 Z" fill="currentColor" opacity={dir === 'desc' ? 1 : 0.35} />
    </svg>
  </span>
);

/**
 * One header cell. Pass `sortKey` to make it sortable; leave it out for the
 * columns that cannot be ordered meaningfully (the row number, Actions).
 */
export const SortTh = ({ label, sortKey, state, style }) => {
  const sortable = !!sortKey && !!state;
  const active = sortable && state.sortKey === sortKey;
  return (
    <th
      onClick={sortable ? (e) => { e.stopPropagation(); state.onSort(sortKey); } : undefined}
      title={sortable ? `Sort by ${label}` : undefined}
      style={{
        padding: '12px', textAlign: 'center', fontWeight: 700,
        cursor: sortable ? 'pointer' : 'default',
        userSelect: 'none', whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {label}
        {sortable && <Chevrons dir={active ? state.sortDir : null} />}
      </span>
    </th>
  );
};
